import React, { useState, useEffect } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import Meyda from "meyda";
import AcceleratedVideoProcessor from "./utils/acceleratedVideoProcessor";
import analytics from "./utils/analytics";
import FooterAdd from "./components/FooterAdd";
import HeaderAdd from "./components/HeaderAdd";

const ffmpeg = new FFmpeg({ log: true });

function App() {
  const [images, setImages] = useState([]);
  const [audio, setAudio] = useState(null);
  const [status, setStatus] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [acceleratedProcessor] = useState(
    () => new AcceleratedVideoProcessor()
  );
  const [useGPUAcceleration, setUseGPUAcceleration] = useState(
    process.env.REACT_APP_ENABLE_GPU_ACCELERATION === "true"
  );
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [progress, setProgress] = useState(0);

  // Define isProcessing before using it in useEffect
  const isProcessing =
    status.includes("Processing") ||
    status.includes("Loading") ||
    status.includes("Initializing") ||
    status.includes("Detecting") ||
    status.includes("Preprocessing") ||
    status.includes("Analyzing") ||
    status.includes("Generating") ||
    status.includes("Encoding") ||
    (status &&
      !status.includes("Done!") &&
      !status.includes("Error") &&
      status !== "");

  // Timer effect for elapsed time
  useEffect(() => {
    let interval;
    if (isProcessing && startTime) {
      interval = setInterval(() => {
        setElapsedTime((Date.now() - startTime) / 1000);
      }, 100); // Update every 100ms for smooth display
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, startTime]);

  // Initialize AudioContext on first user interaction
  const initAudioContext = () => {
    if (!audioContext) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      setAudioContext(ctx);
      return ctx;
    }
    return audioContext;
  };

  const loadFFmpeg = async () => {
    if (!ffmpeg.loaded) {
      setStatus("Loading FFmpeg...");
      setProgress(2);
      try {
        await ffmpeg.load({
          coreURL: `${window.location.origin}/ffmpeg/ffmpeg-core.js`,
          wasmURL: `${window.location.origin}/ffmpeg/ffmpeg-core.wasm`,
        });
        setStatus("FFmpeg loaded!");
        setProgress(5);
        setDebugInfo("FFmpeg loaded successfully");

        // Test FFmpeg by running a simple command
        console.log("Testing FFmpeg...");
        await ffmpeg.exec(["-version"]);
        console.log("FFmpeg is working!");
      } catch (error) {
        console.error("FFmpeg loading error:", error);
        setStatus("FFmpeg loading failed!");
        setDebugInfo(`FFmpeg error: ${error.message}`);
        throw error;
      }
    }
  };

  const handleImageUpload = (e) => {
    const files = [...e.target.files];
    setImages(files);

    // Track image upload
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
      analytics.trackFileUpload("images", files.length, totalSize);
      analytics.trackUserInteraction("image_upload", {
        file_count: files.length,
        total_size_mb: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
      });
    }
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    setAudio(file);

    // Track audio upload
    if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
      analytics.trackFileUpload("audio", 1, file.size);
      analytics.trackUserInteraction("audio_upload", {
        file_size_mb: Math.round((file.size / (1024 * 1024)) * 100) / 100,
        file_type: file.type,
      });
    }
  };

  const detectBeats = async (file) => {
    try {
      const audioCtx = initAudioContext();
      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }

      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      const channelData = audioBuffer.getChannelData(0);

      let beats = [];
      const frameSize = 1024;
      const hopSize = 256; // Smaller hop size for better precision
      const threshold = 0.04; // Adjusted threshold for better beat detection

      console.log(
        `Audio duration: ${audioBuffer.duration}s, Sample rate: ${audioBuffer.sampleRate}`
      );

      // Enhanced beat detection using energy-based approach
      const energyHistory = [];
      const windowSize = 43; // ~1 second of frames for local average

      for (let i = 0; i + frameSize <= channelData.length; i += hopSize) {
        const frame = channelData.slice(i, i + frameSize);

        // Calculate instant energy (RMS)
        let energy = 0;
        for (let j = 0; j < frame.length; j++) {
          energy += frame[j] * frame[j];
        }
        energy = Math.sqrt(energy / frame.length);

        // Store energy for local average calculation
        energyHistory.push(energy);

        // Only start detecting after we have enough history
        if (energyHistory.length >= windowSize) {
          // Calculate local average energy
          const recentEnergy = energyHistory.slice(-windowSize);
          const localAverage =
            recentEnergy.reduce((sum, e) => sum + e, 0) / windowSize;

          // Calculate variance for dynamic threshold
          const variance =
            recentEnergy.reduce(
              (sum, e) => sum + Math.pow(e - localAverage, 2),
              0
            ) / windowSize;
          const dynamicThreshold = localAverage + Math.sqrt(variance) * 1.5;

          // Detect beat if current energy is significantly above local average
          if (energy > dynamicThreshold && energy > threshold) {
            const timePosition = i / audioBuffer.sampleRate;

            // Avoid beats too close together (minimum 0.08 second apart for tighter sync)
            if (
              beats.length === 0 ||
              timePosition - beats[beats.length - 1] > 0.08
            ) {
              beats.push(timePosition);
              console.log(
                `Beat detected at ${timePosition.toFixed(
                  2
                )}s (energy: ${energy.toFixed(
                  4
                )}, threshold: ${dynamicThreshold.toFixed(4)})`
              );
            }
          }
        }
      }

      // If very few beats detected, use spectral flux method as backup
      if (beats.length < 10) {
        console.log(
          "Few beats detected with energy method, trying spectral flux..."
        );
        beats = await detectBeatsSpectralFlux(
          channelData,
          audioBuffer.sampleRate
        );
      }

      // If still no beats detected, create default timing but more frequent
      if (beats.length === 0) {
        console.log(
          "No beats detected with any method, using default fast timing"
        );
        const duration = Math.min(audioBuffer.duration, 60);
        // Create beats every 0.5 seconds for more responsive changes
        for (let i = 0; i < duration; i += 0.5) {
          beats.push(i);
        }
      }

      // Ensure we don't have too many beats (limit to max 120 in 60 seconds)
      if (beats.length > 120) {
        const step = Math.ceil(beats.length / 120);
        beats = beats.filter((_, index) => index % step === 0);
      }

      console.log(
        `Final beats array (${beats.length} beats):`,
        beats.slice(0, 15)
      );

      // Track beat detection
      if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
        analytics.trackBeatDetection(
          beats.length,
          audioBuffer.duration,
          "energy_based"
        );
      }

      return beats;
    } catch (error) {
      console.error("Beat detection error:", error);
      // Fallback: create beats every 0.6 seconds for responsive changes
      const fallbackBeats = [];
      for (let i = 0; i < 60; i += 0.6) {
        fallbackBeats.push(i);
      }
      return fallbackBeats;
    }
  };

  // Backup beat detection using spectral flux
  const detectBeatsSpectralFlux = async (channelData, sampleRate) => {
    const beats = [];
    const frameSize = 1024;
    const hopSize = 512;
    let previousSpectrum = new Array(frameSize / 2).fill(0);

    for (let i = 0; i + frameSize <= channelData.length; i += hopSize) {
      const frame = channelData.slice(i, i + frameSize);

      // Simple FFT approximation using Meyda
      const spectrum = Meyda.extract(
        ["spectralCentroid", "spectralRolloff", "rms"],
        frame
      );

      if (spectrum.rms && spectrum.spectralCentroid) {
        // Calculate spectral flux (difference between consecutive frames)
        const flux =
          Math.abs(spectrum.spectralCentroid - (previousSpectrum[0] || 0)) +
          Math.abs(spectrum.rms - (previousSpectrum[1] || 0));

        // Detect peaks in spectral flux
        if (flux > 500 && spectrum.rms > 0.02) {
          // Threshold for spectral change
          const timePosition = i / sampleRate;
          if (
            beats.length === 0 ||
            timePosition - beats[beats.length - 1] > 0.1
          ) {
            beats.push(timePosition);
          }
        }

        previousSpectrum = [spectrum.spectralCentroid, spectrum.rms];
      }
    }

    return beats;
  };

  const generateReel = async () => {
    if (!images.length || !audio) {
      alert("Please upload images and audio!");
      return;
    }

    try {
      // Reset and start timer
      setStartTime(Date.now());
      setProgress(0);
      setElapsedTime(0);

      // Track processing start
      const method = useGPUAcceleration ? "gpu_accelerated" : "traditional_cpu";
      if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
        analytics.trackProcessingStart(method, images.length, 60); // Assuming 60s audio
        analytics.trackUserInteraction("generate_reel_clicked", {
          processing_method: method,
          image_count: images.length,
        });
      }

      if (useGPUAcceleration) {
        await generateAcceleratedReel();
      } else {
        await generateTraditionalReel();
      }
    } catch (error) {
      console.error("Error generating reel:", error);
      setStatus(`Error: ${error.message}`);
      setProgress(0);
      setStartTime(null);

      // Track error
      if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
        analytics.trackError("processing_error", error.message, {
          processing_method: useGPUAcceleration ? "gpu" : "cpu",
          image_count: images.length,
        });
      }
    }
  };

  const generateAcceleratedReel = async () => {
    setStatus("Initializing GPU acceleration...");
    setProgress(5);
    const initialized = await acceleratedProcessor.initialize();

    if (!initialized) {
      console.warn("GPU acceleration failed, falling back to CPU processing");
      setUseGPUAcceleration(false);
      await generateTraditionalReel();
      return;
    }

    setStatus("Preprocessing images with GPU...");
    setProgress(15);
    setDebugInfo("Loading and resizing images for optimal GPU processing...");
    const processedImages = await acceleratedProcessor.preprocessImages(images);

    setStatus("Analyzing audio with enhanced beat detection...");
    setProgress(30);
    setDebugInfo("Using GPU-accelerated FFT for precise beat detection...");
    const { beatIntensities, duration } =
      await acceleratedProcessor.analyzeAudioWithIntensity(audio);

    setStatus("Generating beat segments...");
    setProgress(45);
    setDebugInfo(
      `Found ${beatIntensities.length} beats, creating dynamic transitions...`
    );
    const segments = acceleratedProcessor.generateBeatSegments(
      beatIntensities,
      processedImages
    );

    setStatus("Generating optimized video segments...");
    setProgress(60);
    setDebugInfo("Creating direct video segments for maximum speed...");
    const frameData = await acceleratedProcessor.generateAcceleratedFrames(
      processedImages,
      segments
    );

    setStatus("Encoding final video...");
    setProgress(75);
    setDebugInfo("Directly encoding segments with FFmpeg...");
    await encodeSegmentsToVideo(frameData, processedImages, duration);
  };

  const encodeSegmentsToVideo = async (
    frameData,
    processedImages,
    duration
  ) => {
    await loadFFmpeg();
    setProgress(80);

    if (frameData.type === "segments") {
      setDebugInfo("Using ultra-fast segment-based encoding...");

      // Write images to FFmpeg
      for (let i = 0; i < processedImages.length; i++) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const imageData = processedImages[i].data;

        canvas.width = imageData.width;
        canvas.height = imageData.height;
        ctx.putImageData(imageData, 0, 0);

        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", 0.9)
        );
        const arrayBuffer = await blob.arrayBuffer();
        await ffmpeg.writeFile(`img${i}.jpg`, new Uint8Array(arrayBuffer));
      }

      setProgress(85);
      setDebugInfo("Processing audio...");
      await ffmpeg.writeFile("audio_full.mp3", await fetchFile(audio));
      await ffmpeg.exec([
        "-i",
        "audio_full.mp3",
        "-t",
        "60",
        "-c",
        "copy",
        "-y",
        "audio.mp3",
      ]);

      setProgress(90);
      // Ultra-fast GPU-accelerated video generation
      setDebugInfo("🚀 Using GPU-optimized single-pass generation...");

      try {
        if (frameData.segments.length === 0) {
          await generateGPUSlideshow(processedImages, duration);
        } else {
          await generateGPUBeatVideo(
            frameData.segments,
            processedImages,
            duration
          );
        }
      } catch (error) {
        console.warn("GPU optimized method failed, using fallback:", error);
        setDebugInfo("Using CPU fallback for compatibility...");
        await generateGPUFallback(
          frameData.segments,
          processedImages,
          duration
        );
      }
    }

    setProgress(95);
    setDebugInfo("Finalizing video...");
    const data = await ffmpeg.readFile("output.mp4");
    const url = URL.createObjectURL(
      new Blob([data.buffer], { type: "video/mp4" })
    );

    setVideoUrl(url);
    setProgress(100);
    setStatus("Done!");

    setStartTime(null);

    // Track successful completion
    const videoSize = Math.round(data.length / 1024);
    if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
      analytics.trackProcessingComplete(
        "gpu_accelerated",
        elapsedTime,
        data.length,
        true
      );
      analytics.trackVideoDownload(data.length, elapsedTime);
      analytics.trackUserInteraction("video_generated_successfully", {
        video_size_kb: videoSize,
        processing_time: elapsedTime,
        method: "gpu_accelerated",
      });
    }

    setDebugInfo(
      `Ultra-fast video generated successfully (${videoSize} KB) with optimized processing`
    );
  };

  const generateTraditionalReel = async () => {
    await loadFFmpeg();
    setProgress(10);

    setStatus("Detecting beats...");
    setProgress(20);
    const beats = await detectBeats(audio);

    if (!beats || !Array.isArray(beats) || beats.length === 0) {
      console.error("Beat detection failed or returned invalid data:", beats);
      throw new Error("Beat detection failed - no valid beats found");
    }

    console.log("Beat detection successful:", beats.length, "beats found");
    setStatus("Processing images and audio...");
    setProgress(35);

    // Continue with the rest of the original implementation
    // Limit video to 1 minute (60 seconds)
    const MAX_VIDEO_DURATION = 60;
    const beatsInFirstMinute = beats.filter(
      (beat) => beat <= MAX_VIDEO_DURATION
    );

    // If no beats in first minute, create default beats for 1 minute
    if (beatsInFirstMinute.length === 0) {
      for (let i = 0; i < 60; i += 0.8) {
        beatsInFirstMinute.push(i);
      }
    }

    // Limit the number of clips to prevent memory issues
    const MAX_CLIPS = 20;
    let finalBeats = beatsInFirstMinute;

    if (beatsInFirstMinute.length > MAX_CLIPS) {
      finalBeats = [];
      const step = beatsInFirstMinute.length / MAX_CLIPS;
      for (let i = 0; i < MAX_CLIPS; i++) {
        const index = Math.floor(i * step);
        finalBeats.push(beatsInFirstMinute[index]);
      }
    }

    console.log(`Using ${finalBeats.length} beats within first minute`);
    setDebugInfo(
      `Using ${finalBeats.length} beat transitions (CPU processing)`
    );

    setProgress(50);
    // Write and process audio
    await ffmpeg.writeFile("audio_full.mp3", await fetchFile(audio));
    await ffmpeg.exec([
      "-i",
      "audio_full.mp3",
      "-t",
      "60",
      "-c",
      "copy",
      "-y",
      "audio.mp3",
    ]);

    setProgress(60);
    // Write images
    for (let i = 0; i < images.length; i++) {
      const imageData = await fetchFile(images[i]);
      await ffmpeg.writeFile(`img${i}.jpg`, imageData);
    }

    setProgress(75);
    // Ultra-fast single-pass video generation with filter_complex
    setDebugInfo("🚀 Using optimized single-pass generation...");

    try {
      await generateOptimizedVideo(finalBeats, images, MAX_VIDEO_DURATION);
    } catch (error) {
      console.warn(
        "Optimized method failed, falling back to compatible method:",
        error
      );
      setDebugInfo("Using fallback method for compatibility...");
      await generateCompatibleVideo(finalBeats, images, MAX_VIDEO_DURATION);
    }

    setProgress(95);
    const data = await ffmpeg.readFile("output.mp4");
    const url = URL.createObjectURL(
      new Blob([data.buffer], { type: "video/mp4" })
    );

    setVideoUrl(url);
    setProgress(100);
    setStatus("Done!");
    setStartTime(null);

    // Track successful completion
    const videoSize = Math.round(data.length / 1024);
    if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
      analytics.trackProcessingComplete(
        "traditional_cpu",
        elapsedTime,
        data.length,
        true
      );
      analytics.trackVideoDownload(data.length, elapsedTime);
      analytics.trackUserInteraction("video_generated_successfully", {
        video_size_kb: videoSize,
        processing_time: elapsedTime,
        method: "traditional_cpu",
      });
    }

    setDebugInfo(`Traditional video generated successfully (${videoSize} KB)`);
  };

  // GPU-optimized beat-synced video generation
  const generateGPUBeatVideo = async (segments, processedImages, duration) => {
    const inputArgs = [];
    const filterParts = [];

    // Ensure we fill 60 seconds - extend segments if needed
    const targetDuration = 60;
    const maxSegments = Math.min(segments.length, 15);
    let useSegments = segments.slice(0, maxSegments);

    // Calculate total duration of detected beats
    const totalBeatDuration = useSegments.reduce(
      (sum, seg) => sum + seg.duration,
      0
    );

    // If beats don't fill 60 seconds, extend the last segment or add more
    if (totalBeatDuration < targetDuration) {
      const remaining = targetDuration - totalBeatDuration;

      if (useSegments.length > 0) {
        // Extend the last segment to fill remaining time
        useSegments[useSegments.length - 1].duration += remaining;
      } else {
        // No segments, create default 60-second slideshow
        const segmentDuration = targetDuration / processedImages.length;
        useSegments = processedImages.map((_, i) => ({
          imageIndex: i,
          duration: segmentDuration,
          startTime: i * segmentDuration,
        }));
      }
    }

    for (let i = 0; i < useSegments.length; i++) {
      const segment = useSegments[i];
      const segmentDuration = Math.max(0.5, Math.min(15.0, segment.duration)); // Allow longer segments

      inputArgs.push(
        "-loop",
        "1",
        "-t",
        segmentDuration.toString(),
        "-i",
        `img${segment.imageIndex}.jpg`
      );
      filterParts.push(
        `[${i}:v]scale=1080:1920:force_original_aspect_ratio=increase,crop=1080:1920,setsar=1[v${i}]`
      );
    }

    // Create smooth transitions between beats
    const concatInputs = filterParts.map((_, i) => `[v${i}]`).join("");
    const concatFilter = `${concatInputs}concat=n=${useSegments.length}:v=1:a=0[outv]`;
    const fullFilter = filterParts.join(";") + ";" + concatFilter;

    setDebugInfo(`⚡ Creating ${targetDuration}s beat-synced video...`);
    await ffmpeg.exec([
      ...inputArgs,
      "-filter_complex",
      fullFilter,
      "-map",
      "[outv]",
      "-r",
      "30",
      "-pix_fmt",
      "yuv420p",
      "-c:v",
      "libx264",
      "-preset",
      "ultrafast",
      "-crf",
      "20",
      "-y",
      "video_no_audio.mp4",
    ]);

    // Add audio - ensure video is exactly 60 seconds
    setDebugInfo("🎵 Syncing audio to 60 seconds...");
    await ffmpeg.exec([
      "-i",
      "video_no_audio.mp4",
      "-i",
      "audio.mp3",
      "-c:v",
      "copy",
      "-c:a",
      "aac",
      "-t",
      "60",
      "-shortest",
      "-y",
      "output.mp4",
    ]);
  };

  // GPU-optimized slideshow for when no beats detected
  const generateGPUSlideshow = async (processedImages, duration) => {
    const maxDuration = Math.min(duration, 60);
    const imageDuration = maxDuration / processedImages.length;
    const fadeTime = Math.min(0.3, imageDuration * 0.2);

    const inputArgs = [];
    const filterParts = [];

    for (let i = 0; i < processedImages.length; i++) {
      inputArgs.push(
        "-loop",
        "1",
        "-t",
        imageDuration.toString(),
        "-i",
        `img${i}.jpg`
      );
      filterParts.push(
        `[${i}:v]scale=1080:1920:force_original_aspect_ratio=increase,crop=1080:1920,setsar=1,fade=in:0:9:fade=out:${Math.floor(
          (imageDuration - fadeTime) * 30
        )}:9[v${i}]`
      );
    }

    const concatFilter =
      processedImages.map((_, i) => `[v${i}]`).join("") +
      `concat=n=${processedImages.length}:v=1:a=0[outv]`;
    const fullFilter = filterParts.join(";") + ";" + concatFilter;

    setDebugInfo("📹 Creating smooth slideshow...");
    await ffmpeg.exec([
      ...inputArgs,
      "-filter_complex",
      fullFilter,
      "-map",
      "[outv]",
      "-r",
      "30",
      "-pix_fmt",
      "yuv420p",
      "-c:v",
      "libx264",
      "-preset",
      "ultrafast",
      "-crf",
      "22",
      "-y",
      "video_no_audio.mp4",
    ]);

    await ffmpeg.exec([
      "-i",
      "video_no_audio.mp4",
      "-i",
      "audio.mp3",
      "-c:v",
      "copy",
      "-c:a",
      "aac",
      "-shortest",
      "-t",
      "60",
      "-y",
      "output.mp4",
    ]);
  };

  // Fallback method for GPU mode
  const generateGPUFallback = async (segments, processedImages, duration) => {
    const useSegments = segments.length > 0 ? segments.slice(0, 8) : [];

    if (useSegments.length === 0) {
      // Simple slideshow fallback
      const segmentDuration =
        Math.min(60, duration) / Math.min(processedImages.length, 6);
      const videoClips = [];

      for (let i = 0; i < Math.min(processedImages.length, 6); i++) {
        const clipName = `fallback${i}.mp4`;
        await ffmpeg.exec([
          "-loop",
          "1",
          "-i",
          `img${i}.jpg`,
          "-t",
          segmentDuration.toString(),
          "-vf",
          "scale=1080:1920:force_original_aspect_ratio=increase,crop=1080:1920",
          "-r",
          "30",
          "-pix_fmt",
          "yuv420p",
          "-preset",
          "ultrafast",
          "-an",
          "-y",
          clipName,
        ]);
        videoClips.push(clipName);
      }

      const concatContent = videoClips
        .map((clip) => `file '${clip}'`)
        .join("\n");
      await ffmpeg.writeFile(
        "concat_list.txt",
        new TextEncoder().encode(concatContent)
      );
      await ffmpeg.exec([
        "-f",
        "concat",
        "-safe",
        "0",
        "-i",
        "concat_list.txt",
        "-c",
        "copy",
        "-y",
        "video_no_audio.mp4",
      ]);
    } else {
      // Beat-based fallback with fewer segments
      const videoClips = [];
      for (let i = 0; i < useSegments.length; i++) {
        const segment = useSegments[i];
        const clipName = `beat${i}.mp4`;

        await ffmpeg.exec([
          "-loop",
          "1",
          "-i",
          `img${segment.imageIndex}.jpg`,
          "-t",
          Math.max(1.0, Math.min(segment.duration, 5.0)).toString(),
          "-vf",
          "scale=1080:1920:force_original_aspect_ratio=increase,crop=1080:1920",
          "-r",
          "30",
          "-pix_fmt",
          "yuv420p",
          "-preset",
          "ultrafast",
          "-an",
          "-y",
          clipName,
        ]);
        videoClips.push(clipName);
      }

      const concatContent = videoClips
        .map((clip) => `file '${clip}'`)
        .join("\n");
      await ffmpeg.writeFile(
        "concat_list.txt",
        new TextEncoder().encode(concatContent)
      );
      await ffmpeg.exec([
        "-f",
        "concat",
        "-safe",
        "0",
        "-i",
        "concat_list.txt",
        "-c",
        "copy",
        "-y",
        "video_no_audio.mp4",
      ]);
    }

    await ffmpeg.exec([
      "-i",
      "video_no_audio.mp4",
      "-i",
      "audio.mp3",
      "-c:v",
      "copy",
      "-c:a",
      "aac",
      "-shortest",
      "-t",
      "60",
      "-y",
      "output.mp4",
    ]);
  };

  // Ultra-fast single-pass video generation using filter_complex
  const generateOptimizedVideo = async (beats, imageFiles, maxDuration) => {
    const inputArgs = [];
    const filterParts = [];
    const targetDuration = 60; // Always create 60-second reels

    // Ensure we have enough beats to fill 60 seconds
    let extendedBeats = [...beats];

    // Calculate total duration from beats
    let totalDuration = 0;
    for (let i = 0; i < beats.length; i++) {
      const startTime = beats[i];
      const endTime =
        i < beats.length - 1
          ? beats[i + 1]
          : Math.min(maxDuration, targetDuration);
      totalDuration = endTime;
    }

    // If beats don't reach 60 seconds, extend with additional beats
    if (totalDuration < targetDuration && beats.length > 0) {
      const interval =
        beats.length > 1
          ? beats[beats.length - 1] - beats[beats.length - 2]
          : 2.0;
      let nextTime = totalDuration;

      while (nextTime < targetDuration) {
        extendedBeats.push(nextTime);
        nextTime += interval;
      }
    }

    // Add all images as inputs with their durations
    for (let i = 0; i < extendedBeats.length; i++) {
      const startTime = extendedBeats[i];
      const endTime =
        i < extendedBeats.length - 1 ? extendedBeats[i + 1] : targetDuration;
      const duration = Math.max(0.5, Math.min(10.0, endTime - startTime)); // Allow longer segments
      const imageIndex = i % imageFiles.length;

      inputArgs.push(
        "-loop",
        "1",
        "-t",
        duration.toString(),
        "-i",
        `img${imageIndex}.jpg`
      );
      filterParts.push(
        `[${i}:v]scale=1080:1920:force_original_aspect_ratio=increase,crop=1080:1920,setsar=1[v${i}]`
      );
    }

    // Create concat filter
    const concatInputs = filterParts.map((_, i) => `[v${i}]`).join("");
    const concatFilter = `${concatInputs}concat=n=${extendedBeats.length}:v=1:a=0[outv]`;
    const fullFilter = filterParts.join(";") + ";" + concatFilter;

    setDebugInfo(
      `⚡ Creating 60-second video with ${extendedBeats.length} segments...`
    );

    // Single command that creates the entire video
    await ffmpeg.exec([
      ...inputArgs,
      "-filter_complex",
      fullFilter,
      "-map",
      "[outv]",
      "-r",
      "30",
      "-pix_fmt",
      "yuv420p",
      "-c:v",
      "libx264",
      "-preset",
      "ultrafast",
      "-crf",
      "23",
      "-y",
      "video_no_audio.mp4",
    ]);

    // Add audio in single step - ensure exactly 60 seconds
    setDebugInfo("🎵 Adding 60-second audio track...");
    await ffmpeg.exec([
      "-i",
      "video_no_audio.mp4",
      "-i",
      "audio.mp3",
      "-c:v",
      "copy",
      "-c:a",
      "aac",
      "-b:a",
      "128k",
      "-t",
      "60",
      "-y",
      "output.mp4",
    ]);
  };

  // Fallback method with reduced segments for compatibility
  const generateCompatibleVideo = async (beats, imageFiles, maxDuration) => {
    setDebugInfo("Creating optimized segments...");

    // Reduce number of segments for speed
    const maxSegments = 10;
    const step = Math.max(1, Math.floor(beats.length / maxSegments));
    const reducedBeats = beats
      .filter((_, index) => index % step === 0)
      .slice(0, maxSegments);

    const videoClips = [];
    for (let i = 0; i < reducedBeats.length; i++) {
      const startTime = reducedBeats[i];
      const endTime =
        i < reducedBeats.length - 1 ? reducedBeats[i + 1] : maxDuration;
      const duration = Math.max(1.0, Math.min(6.0, endTime - startTime)); // Longer segments
      const imageIndex = i % imageFiles.length;
      const clipName = `seg${i}.mp4`;

      await ffmpeg.exec([
        "-loop",
        "1",
        "-i",
        `img${imageIndex}.jpg`,
        "-t",
        duration.toString(),
        "-vf",
        "scale=1080:1920:force_original_aspect_ratio=increase,crop=1080:1920",
        "-r",
        "30",
        "-pix_fmt",
        "yuv420p",
        "-preset",
        "ultrafast",
        "-an",
        "-y",
        clipName,
      ]);

      videoClips.push(clipName);

      if (i % 3 === 0) {
        setDebugInfo(
          `Creating segments: ${Math.round(
            ((i + 1) / reducedBeats.length) * 100
          )}%`
        );
      }
    }

    // Quick concat and audio merge
    const concatContent = videoClips.map((clip) => `file '${clip}'`).join("\n");
    await ffmpeg.writeFile(
      "concat_list.txt",
      new TextEncoder().encode(concatContent)
    );

    await ffmpeg.exec([
      "-f",
      "concat",
      "-safe",
      "0",
      "-i",
      "concat_list.txt",
      "-c",
      "copy",
      "-y",
      "video_no_audio.mp4",
    ]);
    await ffmpeg.exec([
      "-i",
      "video_no_audio.mp4",
      "-i",
      "audio.mp3",
      "-c:v",
      "copy",
      "-c:a",
      "aac",
      "-shortest",
      "-t",
      "60",
      "-y",
      "output.mp4",
    ]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 font-inter">
      <div className="max-w-4xl w-full mx-auto px-3 sm:px-4 md:px-5">
        <HeaderAdd />

        {/* Header - Mobile optimized */}
        <div className="bg-white/95 backdrop-blur-lg rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-4 md:p-8 mb-4 sm:mb-6 md:mb-8 text-center shadow-lg md:shadow-xl">
          <h1 className="text-xl sm:text-2xl md:text-5xl font-bold mb-1 sm:mb-2 md:mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            🎬 Reel Maker
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed px-2">
            Create stunning 60-second vertical reels with images synced to your
            music
          </p>
        </div>

        {/* Main Content Card - Mobile optimized */}
        <div className="bg-white/95 backdrop-blur-lg rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-4 md:p-10 shadow-lg md:shadow-xl">
          {/* Status and Progress - Mobile optimized */}
          {status && (
            <div className="mb-4 sm:mb-6 md:mb-8">
              <div className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-center md:justify-between mb-2 md:mb-4">
                <span className="text-sm sm:text-base md:text-lg font-semibold text-gray-700">
                  Status
                </span>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2 md:gap-4">
                  {isProcessing && (
                    <span className="text-xs sm:text-sm text-gray-500 font-medium">
                      ⏱️ {Math.floor(elapsedTime / 60)}:
                      {Math.floor(elapsedTime % 60)
                        .toString()
                        .padStart(2, "0")}
                    </span>
                  )}
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      status.includes("Error")
                        ? "text-red-500"
                        : "text-green-600"
                    }`}
                  >
                    {status}
                  </span>
                </div>
              </div>

              {isProcessing && (
                <div>
                  <div className="flex justify-between items-center mb-1 md:mb-2">
                    <span className="text-xs sm:text-sm text-gray-500 font-medium">
                      Progress
                    </span>
                    <span className="text-xs sm:text-sm text-indigo-600 font-semibold">
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2 md:mb-3">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full transition-all duration-500 ease-in-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 text-center">
                    Please wait while we create your reel...
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Upload Sections - Mobile stacked layout */}
          <div className="flex flex-col gap-3 sm:gap-4 md:grid md:grid-cols-2 md:gap-8 mb-4 sm:mb-6 md:mb-8">
            {/* Images Upload - Mobile optimized */}
            <div
              className={`border-2 border-dashed rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-8 text-center transition-all duration-300 cursor-pointer ${
                images.length > 0
                  ? "bg-green-50 border-green-400"
                  : "bg-gray-50 border-gray-300"
              }`}
            >
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="text-2xl sm:text-3xl md:text-6xl mb-1 sm:mb-2 md:mb-4">
                  {images.length > 0 ? "✅" : "📸"}
                </div>
                <h3 className="text-sm sm:text-base md:text-xl font-semibold text-gray-700 mb-1 md:mb-3">
                  Upload Images
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-500">
                  {images.length > 0
                    ? `${images.length} images selected`
                    : "Click to select multiple images"}
                </p>
              </label>
            </div>

            {/* Audio Upload - Mobile optimized */}
            <div
              className={`border-2 border-dashed rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-8 text-center transition-all duration-300 cursor-pointer ${
                audio
                  ? "bg-orange-50 border-orange-400"
                  : "bg-gray-50 border-gray-300"
              }`}
            >
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioUpload}
                className="hidden"
                id="audio-upload"
              />
              <label htmlFor="audio-upload" className="cursor-pointer">
                <div className="text-2xl sm:text-3xl md:text-6xl mb-1 sm:mb-2 md:mb-4">
                  {audio ? "🎵" : "🎶"}
                </div>
                <h3 className="text-sm sm:text-base md:text-xl font-semibold text-gray-700 mb-1 md:mb-3">
                  Upload Audio
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-500 break-words">
                  {audio ? `${audio.name}` : "Click to select audio file"}
                </p>
              </label>
            </div>
          </div>

          {/* GPU Acceleration Toggle - Mobile optimized */}
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8">
            <label className="flex items-start sm:items-center cursor-pointer gap-2 sm:gap-3 md:gap-4">
              <div className="relative mt-1 sm:mt-0">
                <input
                  type="checkbox"
                  checked={useGPUAcceleration}
                  onChange={(e) => {
                    setUseGPUAcceleration(e.target.checked);
                    if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
                      analytics.trackUserInteraction("gpu_toggle", {
                        enabled: e.target.checked,
                      });
                    }
                  }}
                  className="hidden"
                />
                <div
                  className={`w-8 sm:w-10 md:w-12 h-5 sm:h-6 md:h-7 rounded-full transition-all duration-300 ${
                    useGPUAcceleration ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 bg-white rounded-full absolute top-0.5 transition-all duration-300 shadow-md ${
                      useGPUAcceleration
                        ? "left-3.5 sm:left-4 md:left-6"
                        : "left-0.5"
                    }`}
                  />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-700 mb-0.5">
                  ⚡ GPU Acceleration
                </h4>
                <p className="text-xs sm:text-sm md:text-sm text-gray-500 leading-relaxed">
                  {useGPUAcceleration
                    ? "Enhanced processing with WebGPU acceleration"
                    : "Standard CPU processing for compatibility"}
                </p>
              </div>
            </label>
          </div>

          {/* Generate Button - Mobile optimized */}
          <button
            onClick={generateReel}
            disabled={!images.length || !audio || isProcessing}
            className={`w-full py-3 sm:py-4 md:py-5 px-4 md:px-8 text-base sm:text-lg md:text-xl font-semibold rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 ${
              !images.length || !audio || isProcessing
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isProcessing ? "🔄 Creating Your Reel..." : "🎬 Generate Reel"}
          </button>

          {/* Debug Info - Mobile optimized */}
          {debugInfo && (
            <div className="mt-3 sm:mt-4 md:mt-6 p-2 sm:p-3 md:p-4 bg-slate-50 border border-slate-200 rounded-lg md:rounded-xl text-xs sm:text-sm text-slate-600 leading-relaxed">
              <strong>Processing:</strong> {debugInfo}
            </div>
          )}
        </div>

        {/* Video Result - Mobile optimized */}
        {videoUrl && (
          <div className="bg-white/95 backdrop-blur-lg rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-4 md:p-10 mt-4 sm:mt-6 md:mt-8 text-center shadow-lg md:shadow-xl">
            <h2 className="text-lg sm:text-xl md:text-3xl font-bold text-gray-700 mb-3 sm:mb-4 md:mb-6">
              🎉 Your Reel is Ready!
            </h2>

            {/* Video container - Mobile optimized */}
            <div className="inline-block rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl mb-3 sm:mb-4 md:mb-6 w-full max-w-xs sm:max-w-sm md:max-w-full">
              <video
                controls
                width="100%"
                height="auto"
                className="block w-full h-auto"
                style={{ aspectRatio: "9/16" }}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Total Processing Time - Mobile optimized */}
            {elapsedTime > 0 && videoUrl !== null && (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-400 rounded-lg md:rounded-xl p-2 sm:p-3 md:p-4 mb-3 sm:mb-4 md:mb-5 text-center max-w-xs sm:max-w-sm md:max-w-md mx-auto">
                <div className="flex flex-col items-center justify-center gap-1 text-xs sm:text-sm md:text-base font-semibold text-blue-800">
                  <span>⏱️ Total Processing Time:</span>
                  <span className="text-blue-900 text-sm sm:text-base md:text-lg">
                    {Math.floor(elapsedTime / 60)}:
                    {Math.floor(elapsedTime % 60)
                      .toString()
                      .padStart(2, "0")}
                  </span>
                </div>
              </div>
            )}

            {/* Download button - Mobile optimized */}
            <a
              href={videoUrl}
              download="reel.mp4"
              className="inline-block px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm sm:text-base md:text-lg font-semibold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 no-underline"
            >
              📥 Download Your Reel
            </a>
          </div>
        )}

        {/* Footer Ad */}
        <FooterAdd />
      </div>
    </div>
  );
}

export default App;
